import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Shield, Zap, Clock, Monitor, HeadphonesIcon, BarChart3, Cloud, Users } from 'lucide-react';
import { Button } from '../components/ui/button';

const plans = [
  {
    name: 'Nonprofit',
    price: 8,
    period: '/mo per TV',
    description: 'Affordable digital signage for churches, schools, and community organizations.',
    highlight: false,
    badge: 'COMMUNITY',
    features: [
      'Unlimited slideshows per TV',
      'Image, video & weather slides',
      'YouTube integration',
      'Custom slide timing',
      'Cloud-based management',
      'Remote content updates',
      'Email support',
      'Auto-refresh displays',
    ],
  },
  {
    name: 'Starter',
    price: 15,
    period: '/mo per TV',
    description: 'Perfect for small businesses getting started with digital signage.',
    highlight: false,
    badge: null,
    features: [
      'Everything in Nonprofit',
      'Priority email support',
      'Display health monitoring',
      'Custom display naming',
      'Portrait & landscape modes',
      'Time-based playlists',
      'Backup & restore',
      'Connected display dashboard',
    ],
  },
  {
    name: 'Business',
    price: 25,
    period: '/mo per TV',
    description: 'Full-featured signage for growing businesses that need more control.',
    highlight: true,
    badge: 'MOST POPULAR',
    features: [
      'Everything in Starter',
      'Unlimited pages & displays',
      'Deployment notifications',
      'Multi-location management',
      'Advanced analytics',
      'Priority phone support',
      'Custom branding options',
      'API access',
    ],
  },
  {
    name: 'Enterprise',
    price: null,
    period: 'Custom',
    description: 'Tailored solutions for large organizations with complex signage needs.',
    highlight: false,
    badge: null,
    features: [
      'Everything in Business',
      'Dedicated account manager',
      'Custom integrations',
      'SLA guarantee (99.9% uptime)',
      'On-site installation support',
      'White-label options',
      'Volume discounts',
      'Training & onboarding',
    ],
  },
];

const reasons = [
  {
    icon: Cloud,
    title: '100% Cloud-Based',
    description: 'No expensive hardware or software to install. Manage your displays from anywhere with just a browser.',
  },
  {
    icon: Clock,
    title: 'Set It & Forget It',
    description: 'Auto-refreshing displays with smart caching. Your content stays fresh without manual intervention.',
  },
  {
    icon: Monitor,
    title: 'Any Screen, Any Size',
    description: 'Works on any TV, monitor, or display with a web browser. Portrait or landscape — your choice.',
  },
  {
    icon: Shield,
    title: 'Secure & Reliable',
    description: 'Password-protected admin panels, encrypted connections, and 99.9% uptime so your screens never go dark.',
  },
  {
    icon: Zap,
    title: 'Easy Content Management',
    description: 'Upload images, paste YouTube links, or enable live weather — all from a simple admin panel anyone can use.',
  },
  {
    icon: Users,
    title: 'Built for Real Businesses',
    description: 'From a single screen in a lobby to dozens across multiple locations — FrostByte scales with you.',
  },
];

const PricingPage = () => {
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);

  const getPrice = (monthly) => {
    if (!monthly) return null;
    return annual ? Math.round(monthly * 0.8) : monthly;
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-slate-900/95 backdrop-blur-lg border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <img
                src="https://customer-assets.emergentagent.com/job_frosty-it-hub/artifacts/o2t5vwwy_logo.jpeg"
                alt="FrostByte IT Logo"
                className="h-12 w-12 object-contain"
              />
              <span className="text-2xl font-bold text-white">
                FrostByte<span className="text-cyan-400">IT</span>
              </span>
            </div>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-20 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Simple, Transparent <span className="text-cyan-400">Pricing</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
            No hidden fees. No long-term contracts. Just powerful digital signage that works — pay per screen, cancel anytime.
          </p>

          {/* Annual toggle */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <span className={`text-sm font-medium ${!annual ? 'text-white' : 'text-slate-500'}`}>Monthly</span>
            <button
              onClick={() => setAnnual(!annual)}
              className={`relative w-14 h-7 rounded-full transition-colors ${annual ? 'bg-cyan-500' : 'bg-slate-700'}`}
              data-testid="annual-toggle"
            >
              <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full transition-transform ${annual ? 'translate-x-7' : 'translate-x-0.5'}`} />
            </button>
            <span className={`text-sm font-medium ${annual ? 'text-white' : 'text-slate-500'}`}>
              Annual <span className="text-cyan-400 text-xs">(Save 20%)</span>
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl p-6 flex flex-col ${
                plan.highlight
                  ? 'bg-gradient-to-b from-cyan-500/10 to-slate-900 border-2 border-cyan-500/50 shadow-lg shadow-cyan-500/10'
                  : 'bg-slate-900 border border-slate-800'
              }`}
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
            >
              {plan.badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold ${
                  plan.highlight ? 'bg-cyan-500 text-white' : 'bg-slate-700 text-cyan-400'
                }`}>
                  {plan.badge}
                </div>
              )}

              <h3 className="text-xl font-bold text-white mt-2">{plan.name}</h3>
              <p className="text-slate-400 text-sm mt-2 min-h-[48px]">{plan.description}</p>

              <div className="mt-6 mb-6">
                {plan.price ? (
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">${getPrice(plan.price)}</span>
                    <span className="text-slate-400 text-sm">{plan.period}</span>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">Custom</span>
                    <span className="text-slate-400 text-sm">pricing</span>
                  </div>
                )}
                {annual && plan.price && (
                  <p className="text-cyan-400 text-xs mt-1">
                    ${getPrice(plan.price) * 12}/year (billed annually)
                  </p>
                )}
              </div>

              <ul className="space-y-3 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => navigate(plan.price ? '/sign-up' : '/sign-up?plan=enterprise')}
                className={`mt-8 w-full ${
                  plan.highlight
                    ? 'bg-cyan-500 hover:bg-cyan-600 shadow-lg shadow-cyan-500/20'
                    : 'bg-slate-800 hover:bg-slate-700 border border-slate-700'
                }`}
                data-testid={`select-${plan.name.toLowerCase()}-btn`}
              >
                {plan.price ? 'Get Started' : 'Contact Sales'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose FrostByte */}
      <section className="py-20 px-6 bg-slate-900/50 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Choose <span className="text-cyan-400">FrostByte IT</span>?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              We built our signage platform specifically for small businesses and nonprofits who need professional displays without the enterprise price tag.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reasons.map((reason) => {
              const Icon = reason.icon;
              return (
                <div
                  key={reason.title}
                  className="p-6 rounded-xl bg-slate-900 border border-slate-800 hover:border-cyan-500/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-cyan-500/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{reason.title}</h3>
                  <p className="text-slate-400 text-sm">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {[
              {
                q: 'How does the nonprofit pricing work?',
                a: 'Registered nonprofits, churches, schools, and community organizations qualify for our $8/month per TV rate. Just let us know during sign-up and we\'ll verify your status.',
              },
              {
                q: 'Can I try it before committing?',
                a: 'Absolutely. Contact us and we\'ll set up a free demo so you can see exactly how it works before you pay a dime.',
              },
              {
                q: 'Is there a contract or setup fee?',
                a: 'No contracts, no setup fees. Pay month-to-month and cancel anytime. We keep your business by earning it, not by locking you in.',
              },
              {
                q: 'Can I manage multiple locations?',
                a: 'Yes! Our Business and Enterprise plans support multi-location management from a single master admin dashboard.',
              },
              {
                q: 'What kind of content can I display?',
                a: 'Images, videos, YouTube content, live weather, and more. Upload files or paste URLs — the admin panel makes it simple.',
              },
            ].map((faq) => (
              <div key={faq.q} className="p-6 rounded-xl bg-slate-900 border border-slate-800">
                <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                <p className="text-slate-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-gradient-to-r from-cyan-500/10 to-slate-900 border-t border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to get started?</h2>
          <p className="text-slate-400 mb-8">
            Join businesses and nonprofits across Minnesota who trust FrostByte IT for their digital signage.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/sign-up')}
              className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-3 text-lg shadow-lg shadow-cyan-500/20"
              data-testid="cta-sign-up-btn"
            >
              Sign Up Now <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800 px-8 py-3 text-lg"
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center text-slate-500 text-sm">
          &copy; {new Date().getFullYear()} FrostByte IT. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
